import React from 'react';
import { action, computed, makeObservable, observable } from "mobx";
import { CatalogProxy } from "../services/catalogServiceAPI/catalogProxy";
import { inject, observer } from "mobx-react";
import { CurrentCatalogItemStore } from "../catalog/upsertItem/store/currentItem";
import { UploadVariantAssets } from "./upload";
import { v4 } from 'uuid';
import { Uploading } from '../uploading/uploading';
import { detectMeshFeatures } from '@designwithfriends/babylon-catalog-features';
import { Alert, Button, Col, Row, Space, Spin, Statistic, Tag, Typography } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined, CloudUploadOutlined } from '@ant-design/icons';

const { Title } = Typography;

const readFileAsText = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsText(file);
    });

const invertVariantFilesMapping = (variantFilesMapping) => {
    const invertedMapping = {};

    Object.keys(variantFilesMapping).forEach(variantId =>
        variantFilesMapping[variantId].forEach(file => invertedMapping[file.uuid] = variantId));

    return invertedMapping;
}

@inject('currentUserStore')
@observer
export class UploadAssets extends React.Component {
    @observable loading = true;
    @observable saving = false;
    @observable errorMsg;
    @observable variantsFiles;
    @observable detectedFeaturesByVariant = {};

    catalogProxy = new CatalogProxy();

    @action.bound setLoading = (isLoading) => this.loading = isLoading;
    @action.bound setSaving = (isSaving) => this.saving = isSaving;
    @action.bound setMessage = (msg) => this.errorMsg = msg;

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    @action.bound recomputeDetectedFeatures = async () => {
        if (!this.variantsFiles) return;
        const next = {};
        for (const variantId of Object.keys(this.variantsFiles)) {
            const files = this.variantsFiles[variantId].filter((f) =>
                f.name && f.name.toLowerCase().endsWith('.babylon'));
            let hasCabinetTexture = false;
            let hasCabinetCountertop = false;
            let hasDoorTexture = false;
            let hasWindowTexture = false;
            let hasBackground = false;
            for (const file of files) {
                try {
                    const text = await readFileAsText(file);
                    const parsed = JSON.parse(text);
                    const features = detectMeshFeatures(parsed);
                    if (features.hasCabinetTexture) hasCabinetTexture = true;
                    if (features.hasCabinetCountertop) hasCabinetCountertop = true;
                    if (features.hasDoorTexture) hasDoorTexture = true;
                    if (features.hasWindowTexture) hasWindowTexture = true;
                    if (features.hasBackground) hasBackground = true;
                } catch (e) {
                    // ignore parse errors
                }
            }
            next[variantId] = { hasCabinetTexture, hasCabinetCountertop, hasDoorTexture, hasWindowTexture, hasBackground };
        }
        this.detectedFeaturesByVariant = next;
    };

    @action.bound addFiles = (variantId, files) => {
        files.forEach(file => {
            file.uuid = v4();
            this.variantsFiles[variantId].push(file);
        });
        this.recomputeDetectedFeatures();
    };

    @action.bound deleteFile = (variantId, fileId) => {
        const files = this.variantsFiles[variantId];
        files.splice(files.findIndex(file => file.uuid === fileId), 1);
        this.recomputeDetectedFeatures();
    };

    @computed get files() {
        let flatItemFiles = [];
        Object.keys(this.variantsFiles).forEach((k) => {
                flatItemFiles = [...flatItemFiles, ...this.variantsFiles[k]];
            }
        );

        return flatItemFiles;
    }

    async componentDidMount() {
        const {id} = this.props.match.params;
        if (typeof id === 'undefined') {
            return alert(`Can't upload assets without an item!`);
        }

        const item = await this.catalogProxy.get(id);
        this.currentItem = new CurrentCatalogItemStore(this.props.currentUserStore, item);
        const variantsFiles = {};
        item.variants.forEach(variant => variantsFiles[variant.id] = []);
        this.variantsFiles = variantsFiles;
        this.setLoading(false);
    }

    validate = async () => {
        const variantWithoutFiles = Object.keys(this.variantsFiles).find(key => this.variantsFiles[key].length === 0);
        if (typeof variantWithoutFiles !== 'undefined') {
            return `Some variants don't have files`;
        }

        return null;
    }

    save = async () => {
        this.setSaving(true);
        this.setMessage('');

        const errorMsg = await this.validate();

        if (errorMsg !== null) {
            this.setMessage(errorMsg);
            this.setSaving(false);
            return;
        }

        try {
            await this.catalogProxy.uploadFiles(this.currentItem.id, this.files, invertVariantFilesMapping(this.variantsFiles));
        } catch (e) {
            console.error(`Can't upload files!`);
            console.error(e);
            this.setMessage(e.text);
            this.setSaving(false);
            return;
        }

        this.setSaving(false);
        this.props.history.push(`/dashboard`);
    }

    render() {
        if (this.loading) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                    <Spin size="large" />
                </div>
            );
        }

        return (
            <div style={{ padding: '24px', maxWidth: 900, margin: '0 auto' }}>
                <Space direction="vertical" size={24} style={{ width: '100%' }}>
                    <div>
                        <Title level={3} style={{ margin: 0 }}>{this.currentItem.name}</Title>
                        <div style={{ marginTop: 8 }}>
                            <Tag
                                icon={this.currentItem.isReady ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                                color={this.currentItem.isReady ? 'success' : 'warning'}
                            >
                                {this.currentItem.isReady ? 'Displayed in editor' : 'Not displayed in editor'}
                            </Tag>
                        </div>
                    </div>

                    <Row gutter={[16, 16]}>
                        {this.currentItem.variants.map((variant) => (
                            <Col xs={24} key={variant.id}>
                                <UploadVariantAssets
                                    addFiles={this.addFiles}
                                    deleteFile={this.deleteFile}
                                    variant={variant}
                                    item={this.currentItem}
                                    files={this.variantsFiles[variant.id]}
                                    detectedFeatures={this.detectedFeaturesByVariant[variant.id]}
                                />
                            </Col>
                        ))}
                    </Row>

                    <Space direction="vertical" align="center" style={{ width: '100%' }}>
                        <Row gutter={48} justify="center">
                            <Col>
                                <Statistic title="Variants" value={this.currentItem.variants.length} />
                            </Col>
                            <Col>
                                <Statistic title="Total Files" value={this.files.length} />
                            </Col>
                        </Row>
                        {this.errorMsg && (
                            <Alert type="error" message={this.errorMsg} showIcon style={{ width: '100%' }} />
                        )}
                        <Button
                            type="primary"
                            size="large"
                            icon={<CloudUploadOutlined />}
                            onClick={this.save}
                            loading={this.saving}
                            style={{ width: 230 }}
                        >
                            Save Assets
                        </Button>
                    </Space>
                </Space>

                {this.saving && <Uploading />}
            </div>
        );
    }
}
